import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  computed,
  effect,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Subject, Subscription, combineLatest, filter } from 'rxjs';
import {
  ChartingLibraryWidgetOptions,
  EntityId,
  IChartWidgetApi,
  IChartingLibraryWidget,
} from 'src/assets/charting_library';

import {
  ChartReadyEventModel,
  ChartType,
  CustomChartIndicators,
  IndicatorsVisibilityConfigModel,
} from '@c/doubled-chart/doubled-chart.model';
import { IGetCustomIndicators } from '@c/shared/symbol-chart/symbol-chart.model';
import { TradingLogSymbolSummaryPanelItemValuesModel } from '@c/trading-log/shared';
import { ChartSaveData, Features, Themes } from '@const';
import {
  breakEvenLineColor,
  costBasisLineColor,
  minStrikeDarkLineColor,
  minStrikeLightLineColor,
  rescueLineColors,
} from '@constants/chart';
import { ISymbolData } from '@core/types';
import { ObservableService as ObservableServiceV1 } from '@core1/directives/observable.service';
import { WheelScannerService } from '@m1/wheel/wheel-scanner-tab/wheel-scanner.service';
import { EditionsService } from '@s/editions.service';
import { ObservableService } from '@s/observable.service';
import { ISymbol } from '@s/symbols.service';
import { LineIndicator } from 'src/app/indicators/line.indicator';
import { MinStrikePriceIndicator } from 'src/app/indicators/min-strike-price.indicator';
import { WheelService } from '../wheel.service';

@Component({
  selector: 'app-wheel-chart',
  templateUrl: './wheel-chart.component.html',
  styleUrls: ['./wheel-chart.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WheelChartComponent implements OnInit, OnDestroy {
  protected readonly features = Features;
  protected readonly chartTypes = ChartType;
  protected readonly chartSavedDataKey = ChartSaveData.Wheel;
  protected readonly isDeveloperAccount = this.observableService.isDeveloperAccount.getValue();
  protected readonly currentSymbol$ = this.observableService.wheelSymbol;
  protected readonly resetChart$ = new Subject<void>();

  protected readonly wheelShowSellCallMinStrikeLine = toSignal(this.observableService.wheelShowSellCallMinStrikeLine, {
    injector: this.injector,
  });

  protected readonly sellCallMinStrikePrice = computed(() => {
    const showLine = this.wheelShowSellCallMinStrikeLine();

    if (!showLine) {
      return null;
    }

    return this.scannerService.sellCallMinStrikePrice();
  });

  protected readonly widgetOptionsOverridesFor1DayChart: Partial<ChartingLibraryWidgetOptions> = {};
  protected readonly widgetOptionsOverridesFor15MinChart: Partial<ChartingLibraryWidgetOptions> = {};
  protected readonly widgetOptionsOverridesFor5MinChart: Partial<ChartingLibraryWidgetOptions> = {};
  protected readonly widgetOptionsOverridesFor1MinChart: Partial<ChartingLibraryWidgetOptions> = {};
  protected readonly disabledIndicators: CustomChartIndicators[] = [CustomChartIndicators.RobsIndicators];

  protected readonly indicatorsOverrideFor1DayChart: Partial<IndicatorsVisibilityConfigModel> = {
    [CustomChartIndicators.LowestCloseLine2150200]: true,
  };

  protected readonly tradingLogSymbolSummaryPanelWheelState =
    this.observableService.tradingLogSymbolSummaryPanelWheelState;
  protected breakEvenLine = new LineIndicator('', breakEvenLineColor);
  protected costBasisLine = new LineIndicator('', costBasisLineColor);
  protected rescueLines: Record<string, LineIndicator> = {};

  private activeChart: IChartWidgetApi | null = null;
  private minStrikeIndicator: MinStrikePriceIndicator = new MinStrikePriceIndicator(
    [],
    this.observableServiceV1.wheelFilters.getValue(),
    null,
    this.observableService.theme.getValue(),
  );

  @Input() tradingLogSymbolSummaryPanelItemValuesModel: TradingLogSymbolSummaryPanelItemValuesModel = {};
  public rescueLinesData: Record<string, number | null> = {};

  private readonly currentTheme = toSignal(this.observableService.theme, { injector: this.injector });
  private sellCallMinStrikeLine = null;

  private subscription = new Subscription();

  constructor(
    private observableService: ObservableService,
    private observableServiceV1: ObservableServiceV1,
    private wheelService: WheelService,
    private editionsService: EditionsService,
    private scannerService: WheelScannerService,
    private injector: Injector,
  ) {}

  ngOnInit(): void {
    effect(
      () => {
        const currentTheme = this.currentTheme();
        const sellCallMinStrikePrice = this.sellCallMinStrikePrice();

        this.updateMinStrikePriceLine(sellCallMinStrikePrice?.value, sellCallMinStrikePrice?.label, currentTheme);
      },
      { injector: this.injector },
    );

    this.observableService.theme.subscribe((theme) => {
      this.minStrikeIndicator.currentTheme = theme;
      this.addRescueLines();
    });

    // TODO: use combineLatest and generate ready to use data based on filters and options
    this.observableServiceV1.wheelFilters.subscribe((filters) => {
      this.minStrikeIndicator.symbolFilters = filters;
    });

    this.subscription.add(
      combineLatest([
        this.wheelService.optionsForMinStrike$.pipe(filter((items) => !!items)),
        this.observableService.wheelShowSellPutMinStrikeLine,
      ]).subscribe(([optionsForMinStrike, showLine]) => {
        this.minStrikeIndicator.symbolFullData = showLine ? ([...optionsForMinStrike] as unknown as ISymbolData[]) : [];
      }),
    );

    this.minStrikeIndicator.initialize();
  }

  ngOnDestroy(): void {
    // Cleanup all rescue lines to prevent stale shapes
    Object.keys(this.rescueLines).forEach((key) => {
      this.rescueLines[key]?.removeLine(this.activeChart);
    });

    this.minStrikeIndicator.destroy();
    this.subscription.unsubscribe();
  }

  public getChartShapeIdsToExclude = (): EntityId[] => {
    return [
      this.minStrikeIndicator.minStrikePriceLineId,
      this.breakEvenLine.lineId,
      this.costBasisLine.lineId,
      ...Object.keys(this.rescueLines).map((key) => this.rescueLines[key].lineId),
      this.sellCallMinStrikeLine?.lineId,
    ];
  };

  protected getTimescaleMarks = async (
    activeChart: IChartWidgetApi,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    symbol: ISymbol,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    timeZone: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    bars: unknown[],
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    resolution: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-unsafe-function-type
    onDataCallback: Function, // to use specific fn: (args: ArgsModel) => ResultModel
  ): Promise<unknown[]> => {
    if (!activeChart) {
      return;
    }

    return [];
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected loadChartData = async (symbol: ISymbol): Promise<void> => {};

  protected async getExpectedMoveOptions(): Promise<{
    observableKeyOne: string;
    observableKeyTwo: string;
  }> {
    return Promise.resolve({
      observableKeyOne: 'showExpectedMoveOneOnWheel',
      observableKeyTwo: 'showExpectedMoveTwoOnWheel',
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected async afterSavedDataRestored(chartWidget: IChartingLibraryWidget, chartType: ChartType): Promise<void> {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected getCustomIndicators: IGetCustomIndicators = (PineJS) => {
    return [];
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onSymbolChanged(symbol: ISymbol): void {}

  protected async onChartReady(event: ChartReadyEventModel): Promise<void> {
    if (!event) {
      return;
    }

    this.activeChart = event.chart;
    this.minStrikeIndicator.activeChart = this.activeChart;
    this.addSummaryPanelLines();
    this.addRescueLines();

    const currentTheme = this.currentTheme();
    const sellCallMinStrikePrice = this.sellCallMinStrikePrice();
    this.updateMinStrikePriceLine(sellCallMinStrikePrice?.value, sellCallMinStrikePrice?.label, currentTheme);
  }

  protected redirectToLowHighIndicatorDemoPage(): void {
    this.editionsService.redirectToDemoPage(Features.LowHighIndicator);
  }

  private updateMinStrikePriceLine(value: number, text: string, currentTheme: Themes): void {
    if (this.sellCallMinStrikeLine) {
      this.sellCallMinStrikeLine.removeLine(this.activeChart);
      this.sellCallMinStrikeLine = null;
    }

    const colors = {
      [Themes.Light]: minStrikeLightLineColor,
      [Themes.Dark]: minStrikeDarkLineColor,
    };

    const color = colors[currentTheme];

    this.sellCallMinStrikeLine = new LineIndicator(text, color);
    this.sellCallMinStrikeLine.showLine(this.activeChart, value, {
      showPriceLabel: true,
      labelBelowLine: true,
      fontsize: 12,
      useHorizontalLine: true,
    });
  }

  public addSummaryPanelLines(): void {
    this.breakEvenLine.removeLine(this.activeChart);

    if (this.tradingLogSymbolSummaryPanelItemValuesModel.breakEven) {
      this.breakEvenLine.showLine(this.activeChart, this.tradingLogSymbolSummaryPanelItemValuesModel.breakEven, {
        showPriceLabel: true,
      });
    }

    this.costBasisLine.removeLine(this.activeChart);

    if (this.tradingLogSymbolSummaryPanelItemValuesModel.costBasis) {
      this.costBasisLine.showLine(this.activeChart, this.tradingLogSymbolSummaryPanelItemValuesModel.costBasis, {
        showPriceLabel: true,
      });
    }
  }

  addRescueLines(): void {
    const activeChart = this.activeChart;
    if (!activeChart) {
      return;
    }
  
    Object.keys(this.rescueLines).forEach((key) => {
      this.rescueLines[key]?.removeLine(activeChart);
      delete this.rescueLines[key];
    });
  
    Object.keys(this.rescueLinesData).forEach((key) => {
      const value = this.rescueLinesData[key];
      if (value == null) {
        return;
      }
      const indicator = new LineIndicator(key, rescueLineColors[this.observableService.theme.getValue()]);
      indicator.showLine(activeChart, value, { showPriceLabel: true, labelBelowLine: true });
      this.rescueLines[key] = indicator;
    });
  }
}
